import React, { useState, useEffect, useCallback, useRef } from 'react';
import Sidebar from '../components/sidebar';
import { Card, Typography, Button, Row, Col, Input, Upload, Modal, Collapse, Select, Tabs, Popover } from 'antd';
import { UserOutlined, ArrowLeftOutlined, PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, BoldOutlined, ItalicOutlined, UnderlineOutlined, AlignLeftOutlined, AlignCenterOutlined, AlignRightOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import debounce from 'lodash.debounce';
import ImageUploader from '../components/ImageUploader';
import './CampanaAudiencia.css';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;
const { Option } = Select;

const verificarusuario = "https://api.mrcirp.com/public/campanas-audiencia/existe";

// Componente de Editor de Texto Enriquecido Personalizado
const RichTextEditor = ({ value, onChange, placeholder, maxLength }) => {
  const editorRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const [textLength, setTextLength] = useState(0);
  const [selectedFont, setSelectedFont] = useState('Arial');
  const [selectedSize, setSelectedSize] = useState('p');

  const getTextContent = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    return div.textContent || div.innerText || '';
  };

  // Guardar la selección actual
  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && editorRef.current?.contains(selection.anchorNode)) {
      savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
    } else {
      savedSelectionRef.current = null;
    }
  };

  // Restaurar la selección guardada
  const restoreSelection = () => {
    if (savedSelectionRef.current && editorRef.current) {
      try {
        const selection = window.getSelection();
        selection.removeAllRanges();
        
        // Verificar que el rango sigue siendo válido
        const range = savedSelectionRef.current;
        if (range && editorRef.current.contains(range.commonAncestorContainer)) {
          selection.addRange(range);
          editorRef.current.focus();
          return true;
        } else {
          // Si el rango ya no es válido, intentar restaurar al final del contenido seleccionado
          const clonedRange = range.cloneRange();
          if (editorRef.current.contains(clonedRange.commonAncestorContainer)) {
            selection.addRange(clonedRange);
            editorRef.current.focus();
            return true;
          }
        }
      } catch (e) {
        console.warn('Error al restaurar selección:', e);
      }
    }
    return false;
  };

  const handleInput = (e) => {
    const html = e.target.innerHTML;
    const textContent = getTextContent(html);
    
    if (textContent.length <= maxLength) {
      setTextLength(textContent.length);
      onChange(html);
    } else {
      // Revertir si excede el límite
      e.target.innerHTML = value || '';
    }
  };

  const handleFormat = (command, formatValue = null) => {
    if (!editorRef.current) return;
    
    // Restaurar la selección antes de aplicar formato
    const restored = restoreSelection();
    
    // Obtener el rango actual o guardado
    let range = null;
    const selection = window.getSelection();
    
    if (restored && selection.rangeCount > 0) {
      range = selection.getRangeAt(0);
    } else if (savedSelectionRef.current) {
      // Intentar usar el rango guardado directamente
      range = savedSelectionRef.current;
      if (editorRef.current.contains(range.commonAncestorContainer)) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
    
    // Para fontSize, usar etiquetas HTML semánticas (h1-h6, p)
    if (command === 'fontSize' && formatValue) {
      if (range && !range.collapsed && editorRef.current.contains(range.commonAncestorContainer)) {
        // Asegurar que tenemos el foco
        editorRef.current.focus();
        
        // Crear la etiqueta HTML correspondiente
        const tagName = formatValue; // h1, h2, h3, h4, h5, h6, o p
        const element = document.createElement(tagName);
        
        try {
          // Clonar el rango para no modificar el original
          const clonedRange = range.cloneRange();
          clonedRange.surroundContents(element);
        } catch (e) {
          // Si falla, intentar envolver manualmente
          try {
            const contents = range.extractContents();
            element.appendChild(contents);
            range.insertNode(element);
          } catch (e2) {
            console.warn('Error al aplicar tamaño:', e2);
          }
        }
      } else {
        // Si no hay selección, aplicar al siguiente texto que se escriba
        editorRef.current.focus();
        // Para texto futuro, usar formatBlock
        document.execCommand('formatBlock', false, `<${formatValue}>`);
      }
    } else if (command === 'fontName' && formatValue) {
      // Para fontName, usar execCommand pero asegurar que tenemos selección
      editorRef.current.focus();
      if (range && !range.collapsed) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
      document.execCommand('fontName', false, formatValue);
    } else {
      editorRef.current.focus();
      if (range && !range.collapsed) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
      document.execCommand(command, false, formatValue);
    }
    
    // Actualizar el contenido después de aplicar formato
    const html = editorRef.current?.innerHTML || '';
    const textContent = getTextContent(html);
    setTextLength(textContent.length);
    onChange(html);
    
    // Limpiar la selección guardada después de usarla
    savedSelectionRef.current = null;
  };

  const handleFontChange = (fontFamily) => {
    setSelectedFont(fontFamily);
    // Usar setTimeout para restaurar y aplicar formato después de que el Select cierre
    setTimeout(() => {
      handleFormat('fontName', fontFamily);
    }, 100);
  };

  const handleSizeChange = (size) => {
    setSelectedSize(size);
    // Usar setTimeout para restaurar y aplicar formato después de que el Select cierre
    setTimeout(() => {
      handleFormat('fontSize', size);
    }, 100);
  };

  // Guardar selección cuando el usuario hace clic en el Select (antes de que se pierda)
  const handleSelectMouseDown = (e) => {
    saveSelection();
  };

  const handleColorChange = (color) => {
    handleFormat('foreColor', color);
  };

  // Colores predeterminados (paleta similar a Microsoft Word)
  const predefinedColors = [
    '#000000', '#FFFFFF', '#EEECE1', '#1F497D', '#4F81BD', '#9BBB59', '#8064A2', '#4BACC6', '#F79646', '#C00000',
    '#FF0000', '#FFC000', '#FFFF00', '#92D050', '#00B050', '#00B0F0', '#0070C0', '#002060', '#7030A0', '#C0504D',
    '#FF0066', '#FF99CC', '#FFCCFF', '#FF99FF', '#CC99FF', '#9999FF', '#6699FF', '#3399FF', '#00CCFF', '#00FFCC',
    '#00FF99', '#99FF99', '#CCFF99', '#FFFF99', '#FFCC99', '#FF9999', '#FF6666', '#FF3333', '#CC0000', '#990000'
  ];

  const ColorPicker = () => {
    const [open, setOpen] = useState(false);
    
    const content = (
      <div style={{ width: '280px', padding: '8px' }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(10, 1fr)', 
          gap: '4px',
          marginBottom: '8px'
        }}>
          {predefinedColors.map((color, index) => (
            <div
              key={index}
              onClick={() => {
                handleColorChange(color);
                setOpen(false);
              }}
              style={{
                width: '24px',
                height: '24px',
                backgroundColor: color,
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                cursor: 'pointer',
                boxShadow: color === '#FFFFFF' ? 'inset 0 0 0 1px #d9d9d9' : 'none'
              }}
              title={color}
            />
          ))}
        </div>
        <div style={{ 
          borderTop: '1px solid #e8e8e8', 
          paddingTop: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <input
            type="color"
            onChange={(e) => {
              handleColorChange(e.target.value);
              setOpen(false);
            }}
            style={{
              width: '40px',
              height: '32px',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            title="Color personalizado"
          />
          <span style={{ fontSize: '12px', color: '#666' }}>Color personalizado</span>
        </div>
      </div>
    );

    return (
      <Popover
        content={content}
        trigger="click"
        open={open}
        onOpenChange={setOpen}
        placement="bottomLeft"
      >
        <Button
          type="text"
          size="small"
          style={{
            width: '32px',
            height: '24px',
            padding: 0,
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            backgroundColor: '#000000',
            position: 'relative'
          }}
          title="Color de texto"
        >
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, #ff0000 0%, #ff00ff 25%, #0000ff 50%, #00ffff 75%, #00ff00 100%)',
            borderRadius: '3px'
          }} />
        </Button>
      </Popover>
    );
  };

  // Prevenir pegado de imágenes
  const handlePaste = (e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData || window.clipboardData;
    const pastedData = clipboardData.getData('text/html') || clipboardData.getData('text/plain');
    
    // Si contiene imágenes, solo pegar el texto
    if (pastedData.includes('<img') || pastedData.includes('data:image')) {
      // Solo permitir texto plano
      const text = clipboardData.getData('text/plain');
      document.execCommand('insertText', false, text);
    } else {
      // Permitir pegar HTML sin imágenes
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        
        // Crear un div temporal para limpiar el HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = pastedData;
        
        // Remover todas las imágenes
        const images = tempDiv.querySelectorAll('img');
        images.forEach(img => img.remove());
        
        // Insertar el contenido limpio
        const fragment = document.createDocumentFragment();
        while (tempDiv.firstChild) {
          fragment.appendChild(tempDiv.firstChild);
        }
        range.insertNode(fragment);
        
        // Mover el cursor al final
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
    
    // Actualizar el contenido
    const html = editorRef.current?.innerHTML || '';
    const textContent = getTextContent(html);
    setTextLength(textContent.length);
    onChange(html);
  };

  useEffect(() => {
    if (editorRef.current && value !== undefined) {
      const textContent = getTextContent(value);
      setTextLength(textContent.length);
      // Solo actualizar si el contenido es diferente para evitar loops
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value]);

  return (
    <div className="custom-rich-text-editor">
      <div className="editor-toolbar">
        <Select
          value={selectedFont}
          style={{ width: 120 }}
          onChange={handleFontChange}
          onMouseDown={handleSelectMouseDown}
          onFocus={handleSelectMouseDown}
          size="small"
        >
          <Option value="Arial">Arial</Option>
          <Option value="Times New Roman">Times New Roman</Option>
          <Option value="Courier New">Courier New</Option>
          <Option value="Georgia">Georgia</Option>
          <Option value="Verdana">Verdana</Option>
          <Option value="Comic Sans MS">Comic Sans MS</Option>
        </Select>
        
        <Select
          value={selectedSize}
          style={{ width: 120 }}
          onChange={handleSizeChange}
          onMouseDown={handleSelectMouseDown}
          onFocus={handleSelectMouseDown}
          size="small"
        >
          <Option value="p">Párrafo (p)</Option>
          <Option value="h6">Título 6 (h6)</Option>
          <Option value="h5">Título 5 (h5)</Option>
          <Option value="h4">Título 4 (h4)</Option>
          <Option value="h3">Título 3 (h3)</Option>
          <Option value="h2">Título 2 (h2)</Option>
          <Option value="h1">Título 1 (h1)</Option>
        </Select>

        <Button
          type="text"
          icon={<BoldOutlined />}
          onClick={() => handleFormat('bold')}
          size="small"
          title="Negrita"
        />
        
        <Button
          type="text"
          icon={<ItalicOutlined />}
          onClick={() => handleFormat('italic')}
          size="small"
          title="Cursiva"
        />
        
        <Button
          type="text"
          icon={<UnderlineOutlined />}
          onClick={() => handleFormat('underline')}
          size="small"
          title="Subrayado"
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px', paddingLeft: '8px', borderLeft: '1px solid #e8e8e8' }}>
          <Button
            type="text"
            icon={<AlignLeftOutlined />}
            onClick={() => handleFormat('justifyLeft')}
            size="small"
            title="Alinear izquierda"
          />
          <Button
            type="text"
            icon={<AlignCenterOutlined />}
            onClick={() => handleFormat('justifyCenter')}
            size="small"
            title="Centrar"
          />
          <Button
            type="text"
            icon={<AlignRightOutlined />}
            onClick={() => handleFormat('justifyRight')}
            size="small"
            title="Alinear derecha"
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
          <ColorPicker />
          <span style={{ fontSize: '12px', color: '#666' }}>Color</span>
        </div>
      </div>
      <div
        ref={editorRef}
        contentEditable
        className="editor-content"
        onInput={handleInput}
        onMouseUp={saveSelection}
        onKeyUp={saveSelection}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />
    </div>
  );
};

const CampanaAudiencia = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Detectar si viene de /referidos para mostrar campos de video
  const esReferidos = location.pathname === '/referidos';
  
  // Función para convertir URLs de YouTube/Vimeo al formato de embed
  const convertToEmbedUrl = (url) => {
    if (!url) return '';
    
    // Si ya es una URL de embed, devolverla tal cual
    if (url.includes('youtube.com/embed') || url.includes('youtu.be/embed') || url.includes('player.vimeo.com')) {
      return url;
    }
    
    // YouTube: convertir watch?v= o youtu.be/ a embed
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('watch?v=')[1]?.split('&')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=0&modestbranding=1&rel=0` : url;
    }
    
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=0&modestbranding=1&rel=0` : url;
    }
    
    // Vimeo: usar expresión regular para extraer el ID del video de cualquier formato de URL
    if (url.includes('vimeo.com/')) {
      const vimeoRegex = /https?:\/\/(?:www\.|player\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|album\/(\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/;
      const match = url.match(vimeoRegex);
      if (match && match[3]) {
        return `https://player.vimeo.com/video/${match[3]}?autoplay=0&byline=0&portrait=0`;
      }
      // Fallback: intentar extraer el ID de forma simple
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0]?.split('/').pop();
      return videoId ? `https://player.vimeo.com/video/${videoId}?autoplay=0&byline=0&portrait=0` : url;
    }
    
    // Si no coincide con ningún formato conocido, devolver la URL original
    return url;
  };
  
  // Estados para información básica de la campaña
  const [campanaData, setCampanaData] = useState({
    nombrecampana: '',
    nombre: '',
    descripcion: ''
  });

  const [mensajeVerificacion, setMensajeVerificacion] = useState('');
  const [colorVerificacion, setColorVerificacion] = useState('');
  const [openBasico, setOpenBasico] = useState(true);
  const [activeTab, setActiveTab] = useState('bienvenida'); // Estado para el tab activo
  
  // Estados para el contenido de la landing
  const [landingData, setLandingData] = useState({
    // Banner Principal
    banner: {
      titulo: 'Tu Título Principal Aquí',
      subtitulo: 'Subtítulo descriptivo que capture la atención',
      videoUrl: '',
      imagenUrl: '',
      botonTextoFinalizar: 'Haz click para continuar',
      // Campos para los botones de opciones (solo para campañas de video)
      preguntaTexto: esReferidos ? '¿Qué le pareció el video? … ¿Sorprendente cierto? ¿Como deseas continuar?' : '',
      botonA: esReferidos ? 'SÍ QUIERO MÁS INFORMACIÓN DEL PRODUCTO' : '',
      botonB: esReferidos ? 'QUIERO CONOCER MÁS DEL NEGOCIO' : '',
      botonC: esReferidos ? 'NO GRACIAS, DESEO SALIR' : '',
      botonVolver: esReferidos ? 'VOLVER' : ''
    },
    // Sección de Servicios
    servicios: {
      titulo: 'Nuestros Servicios',
      descripcion: 'Descripción general de los servicios que ofreces',
      items: [
        { titulo: 'Servicio 1', descripcion: 'Descripción del primer servicio' },
        { titulo: 'Servicio 2', descripcion: 'Descripción del segundo servicio' },
        { titulo: 'Servicio 3', descripcion: 'Descripción del tercer servicio' }
      ]
    },
    // Sección de Dos Columnas (imagen izquierda)
    dosColumnas: {
      imagen: '',
      titulo: 'Título de la Sección',
      descripcion: 'Descripción detallada de esta sección'
    },
    // Sección de Dos Columnas (imagen derecha)
    dosColumnasDerecha: {
      imagen: '',
      titulo: 'Título de la Segunda Sección',
      descripcion: 'Descripción detallada de esta segunda sección',
      fondoImagen: ''
    },
    // Campos de video (compatibilidad con Referidos)
    urlVideo: '',
    urlVideoProducto1: '',
    urlVideoProducto2: '',
    urlVideoProducto3: '',
    urlBotonProducto: '',
    urlComoUnirseCliente: '',
    urlNegocio1: '',
    urlNegocio2: '',
    urlNegocio3: '',
    urlBotonNegocio: '',
    urlComoAfiliarse: ''
  });

  const [editingSection, setEditingSection] = useState(null);
  const [previewBannerData, setPreviewBannerData] = useState(null); // Estado temporal para vista previa en tiempo real
  const [previewDosColumnasData, setPreviewDosColumnasData] = useState(null); // Estado temporal para vista previa de dos columnas
  const [previewDosColumnasDerechaData, setPreviewDosColumnasDerechaData] = useState(null); // Estado temporal para vista previa de dos columnas derecha
  
  // Estados para Landing de Revelación y Éxito (solo para campañas de video/referidos)
  const [revelacionLandingData, setRevelacionLandingData] = useState({
    banner: {
      titulo: 'Tu Título Principal Aquí',
      subtitulo: 'Subtítulo descriptivo que capture la atención',
      videoUrl: '',
      imagenUrl: ''
    },
    servicios: {
      titulo: 'Nuestros Servicios',
      descripcion: 'Descripción general de los servicios que ofreces',
      items: [
        { titulo: 'Servicio 1', descripcion: 'Descripción del primer servicio' },
        { titulo: 'Servicio 2', descripcion: 'Descripción del segundo servicio' },
        { titulo: 'Servicio 3', descripcion: 'Descripción del tercer servicio' }
      ],
      fondoImagen: ''
    },
    dosColumnas: {
      imagen: '',
      titulo: 'Título de la Sección',
      descripcion: 'Descripción detallada de esta sección',
      fondoImagen: ''
    },
    dosColumnasDerecha: {
      imagen: '',
      titulo: 'Título de la Segunda Sección',
      descripcion: 'Descripción detallada de esta segunda sección',
      fondoImagen: ''
    }
  });

  const [exitoLandingData, setExitoLandingData] = useState({
    banner: {
      titulo: 'Tu Título Principal Aquí',
      subtitulo: 'Subtítulo descriptivo que capture la atención',
      videoUrl: '',
      imagenUrl: ''
    },
    servicios: {
      titulo: 'Nuestros Servicios',
      descripcion: 'Descripción general de los servicios que ofreces',
      items: [
        { titulo: 'Servicio 1', descripcion: 'Descripción del primer servicio' },
        { titulo: 'Servicio 2', descripcion: 'Descripción del segundo servicio' },
        { titulo: 'Servicio 3', descripcion: 'Descripción del tercer servicio' }
      ],
      fondoImagen: ''
    },
    dosColumnas: {
      imagen: '',
      titulo: 'Título de la Sección',
      descripcion: 'Descripción detallada de esta sección',
      fondoImagen: ''
    },
    dosColumnasDerecha: {
      imagen: '',
      titulo: 'Título de la Segunda Sección',
      descripcion: 'Descripción detallada de esta segunda sección',
      fondoImagen: ''
    }
  });
  
  // Estado para el landing de WhatsApp (solo para campañas de audiencia)
  const [whatsappLandingData, setWhatsappLandingData] = useState({
    titulo: '',
    subtitulo: '',
    imagenUrl: '',
    videoUrl: '',
    botonTexto: 'Dale Click Aquí',
    botonUrl: ''
  });

  // ========== SISTEMA DE BORRADORES ==========
  const BORRADOR_KEY = esReferidos ? 'borrador_campana_video' : 'borrador_campana_audiencia';
  
  // Función para guardar borrador
  const guardarBorrador = useCallback(() => {
    try {
      const borrador = {
        campanaData,
        landingData,
        revelacionLandingData,
        exitoLandingData,
        whatsappLandingData,
        activeTab,
        timestamp: new Date().toISOString()
      };
      window.localStorage.setItem(BORRADOR_KEY, JSON.stringify(borrador));
      setHayBorrador(true);
      console.log('💾 Borrador guardado automáticamente');
    } catch (error) {
      console.error('Error al guardar borrador:', error);
    }
  }, [campanaData, landingData, revelacionLandingData, exitoLandingData, whatsappLandingData, activeTab, BORRADOR_KEY]);
  
  // Función para cargar borrador
  const cargarBorrador = useCallback(() => {
    try {
      const borradorJSON = window.localStorage.getItem(BORRADOR_KEY);
      if (borradorJSON) {
        const borrador = JSON.parse(borradorJSON);
        
        // Verificar que el borrador no sea muy antiguo (máximo 7 días)
        const timestamp = new Date(borrador.timestamp);
        const ahora = new Date();
        const diasDiferencia = (ahora - timestamp) / (1000 * 60 * 60 * 24);
        
        if (diasDiferencia > 7) {
          // Borrar borrador antiguo
          window.localStorage.removeItem(BORRADOR_KEY);
          return false;
        }
        
        // Cargar datos del borrador
        if (borrador.campanaData) setCampanaData(borrador.campanaData);
        if (borrador.landingData) setLandingData(borrador.landingData);
        if (borrador.revelacionLandingData) setRevelacionLandingData(borrador.revelacionLandingData);
        if (borrador.exitoLandingData) setExitoLandingData(borrador.exitoLandingData);
        if (borrador.whatsappLandingData) setWhatsappLandingData(borrador.whatsappLandingData);
        if (borrador.activeTab) setActiveTab(borrador.activeTab);
        
        console.log('📂 Borrador cargado automáticamente');
        return true;
      }
    } catch (error) {
      console.error('Error al cargar borrador:', error);
    }
    return false;
  }, [BORRADOR_KEY]);
  
  // Función para limpiar borrador
  const limpiarBorrador = useCallback(() => {
    try {
      window.localStorage.removeItem(BORRADOR_KEY);
      setHayBorrador(false);
      console.log('🗑️ Borrador limpiado');
    } catch (error) {
      console.error('Error al limpiar borrador:', error);
    }
  }, [BORRADOR_KEY]);
  
  // Cargar borrador al montar el componente
  useEffect(() => {
    const borradorCargado = cargarBorrador();
    if (borradorCargado) {
      setHayBorrador(true);
      // Mostrar mensaje de que se cargó un borrador
      setTimeout(() => {
        const confirmar = window.confirm('💾 Se encontró un borrador guardado.\n\n¿Deseas continuar desde donde lo dejaste?\n\n(Clic en "Cancelar" para empezar desde cero)');
        if (!confirmar) {
          limpiarBorrador();
          setHayBorrador(false);
        }
      }, 500);
    }
  }, []); // Solo al montar
  
  // Guardar borrador automáticamente cuando cambien los datos (con debounce)
  const guardarBorradorDebounced = useCallback(
    debounce(() => {
      guardarBorrador();
    }, 2000), // Guardar 2 segundos después del último cambio
    [guardarBorrador]
  );
  
  useEffect(() => {
    guardarBorradorDebounced();
  }, [campanaData, landingData, revelacionLandingData, exitoLandingData, whatsappLandingData, activeTab, guardarBorradorDebounced]);
  // ========== FIN SISTEMA DE BORRADORES ==========

  const verificarNombre = useCallback(debounce(async (nombre) => {
    try {
      console.log(`${verificarusuario}/${nombre}`);
      const response = await axios.get(`${verificarusuario}/${nombre}`);
      if (response.data === true) {
        setMensajeVerificacion('Enlace existente');
        setColorVerificacion('red');
      } else {
        setMensajeVerificacion('Enlace disponible');
        setColorVerificacion('green');
      }
    } catch (err) {
      console.error(err);
    }
  }, 500), []);

  const handleChangeCampana = (e) => {
    const { name, value } = e.target;
    setCampanaData(prevState => ({
      ...prevState,
      [name]: value
    }));

    if (name === 'nombre') {
      verificarNombre(value);
    }
  };

  // Función para convertir base64 a archivo y subirlo al servidor
  const uploadBase64Image = async (base64String, imageType = 'campaign') => {
    if (!base64String || !base64String.startsWith('data:image/')) {
      // Si no es base64, devolver la URL original
      return base64String;
    }

    try {
      // Convertir base64 a blob
      const response = await fetch(base64String);
      const blob = await response.blob();
      
      // Determinar la extensión del archivo desde el tipo MIME
      const mimeType = base64String.match(/data:image\/(\w+);base64/)?.[1] || 'jpg';
      const extension = mimeType === 'jpeg' ? 'jpg' : mimeType;
      
      // Crear un archivo desde el blob
      const fileName = `campaign_${Date.now()}_${Math.random().toString(36).substring(7)}.${extension}`;
      const file = new File([blob], fileName, { type: `image/${mimeType}` });
      
      // Crear FormData para subir el archivo
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', imageType);
      
      // Subir al servidor
      const uploadResponse = await axios.post(
        'https://api.mrcirp.com/public/upload-imagen-campana',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      // Extraer la URL de la respuesta
      let imageUrl = null;
      if (uploadResponse.data && uploadResponse.data.url) {
        imageUrl = uploadResponse.data.url;
      } else if (uploadResponse.data && uploadResponse.data.imagen) {
        imageUrl = uploadResponse.data.imagen;
      } else if (uploadResponse.data && typeof uploadResponse.data === 'string') {
        imageUrl = uploadResponse.data;
      }
      
      if (imageUrl) {
        console.log(`✅ Imagen subida exitosamente: ${imageUrl}`);
        return imageUrl;
      } else {
        console.warn('⚠️ No se recibió URL del servidor, usando base64 original');
        return base64String;
      }
    } catch (error) {
      console.error('❌ Error al subir imagen base64:', error);
      // Si falla la subida, devolver la URL original (base64)
      return base64String;
    }
  };

  // Función para procesar todas las URLs de imágenes en los datos y subir las base64
  const processImagesInData = async (data) => {
    const processedData = { ...data };
    
    // Lista de campos que pueden contener URLs de imágenes
    const imageFields = [
      'banner_imagen_url',
      'dos_columnas_imagen',
      'dos_columnas_derecha_imagen',
      'servicios_fondo_imagen',
      'dos_columnas_fondo_imagen',
      'whatsapp_imagen_url',
      'revelacion_banner_imagen_url',
      'revelacion_dos_columnas_imagen',
      'revelacion_dos_columnas_derecha_imagen',
      'revelacion_dos_columnas_derecha_fondo_imagen',
      'revelacion_servicios_fondo_imagen',
      'revelacion_dos_columnas_fondo_imagen',
      'exito_banner_imagen_url',
      'exito_dos_columnas_imagen',
      'exito_dos_columnas_derecha_imagen',
      'exito_dos_columnas_derecha_fondo_imagen',
      'exito_servicios_fondo_imagen',
      'exito_dos_columnas_fondo_imagen'
    ];
    
    // Procesar cada campo de imagen
    for (const field of imageFields) {
      if (processedData[field] && processedData[field].startsWith('data:image/')) {
        console.log(`📤 Procesando imagen base64 en campo: ${field}`);
        processedData[field] = await uploadBase64Image(processedData[field]);
      }
    }
    
    return processedData;
  };

  const handleCrearCampana = async () => {
    try {
      // Validar campos obligatorios
      if (!campanaData.nombrecampana || campanaData.nombrecampana.trim() === '') {
        alert('Por favor, ingresa el nombre de la campaña');
        return;
      }
      
      if (!campanaData.nombre || campanaData.nombre.trim() === '') {
        alert('Por favor, ingresa el enlace de la campaña');
        return;
      }
      
      if (!campanaData.descripcion || campanaData.descripcion.replace(/<[^>]*>/g, '').trim() === '') {
        alert('Por favor, ingresa la descripción de la campaña');
        return;
      }

      console.log('=== INICIO CREAR CAMPAÑA ===');
      console.log('esReferidos:', esReferidos);
      console.log('campanaData:', campanaData);
      console.log('landingData:', landingData);

      // Obtener datos del usuario logueado
      const loginUserJSON = window.localStorage.getItem('loginUser');
      let adminId = null;
      let adminNombre = '';

      if (loginUserJSON) {
        const user = JSON.parse(loginUserJSON);
        adminId = user.id;
        adminNombre = user.nombreuser || user.nombre || 'Usuario';
        console.log('Usuario logueado - ID:', adminId, 'Nombre:', adminNombre);
      } else {
        console.warn('⚠️ No se encontró usuario logueado');
      }

      // Validar límite de campañas según membresía
      if (adminId) {
        try {
          // Obtener campañas de video (usuarios) - mismo método que en Mislanding
          const responseVideo = await axios.get('https://api.mrcirp.com/public/usuarios');
          const filteredVideoData = responseVideo.data.filter(item => item.creador === adminId);
          
          // Obtener campañas de audiencia - mismo método que en Mislanding
          const responseAudiencia = await axios.get('https://api.mrcirp.com/public/campanas-audiencia');
          const filteredAudienciaData = responseAudiencia.data.filter(item => item.admin_id === adminId);
          
          const total = filteredVideoData.length + filteredAudienciaData.length;
          
          // Obtener membresía del usuario
          const userResponse = await axios.get(`https://api.mrcirp.com/public/userpanel/${adminId}`);
          const userData = userResponse.data && userResponse.data[0] ? userResponse.data[0] : null;
          const membresia = userData?.membresia || null;
          
          // Definir límites según membresía
          const limites = {
            'Katana Demo': 1,
            'Katana Basic': 2,
            'Katana Pro': 5,
            'Katana Premium': 10
          };
          
          if (membresia && limites[membresia]) {
            const limite = limites[membresia];
            if (total >= limite) {
              const mensaje = `Has alcanzado el límite de campañas para tu membresía (${membresia}).\n\nLímite: ${limite} campañas\nActualmente tienes: ${total} campañas\n\nPor favor, actualiza tu membresía para crear más campañas.`;
              alert(mensaje);
              return;
            }
          }
        } catch (error) {
          console.warn('Error al verificar límite de campañas:', error);
          // Continuar si hay error (no bloquear)
        }
      }

      let datosCompletos;
      let endpoint;

      if (esReferidos) {
        // Si viene de /referidos, crear campaña de video usando endpoint de usuarios
        console.log('📹 Creando campaña de VIDEO');
        datosCompletos = {
          nombrecampana: campanaData.nombrecampana,
          nombre: campanaData.nombre,
          usuario: campanaData.nombre, // Asegurar que también se guarde como 'usuario' para que el endpoint lo encuentre
          descripcion: campanaData.descripcion,
          // Campos de banner
          banner_titulo: landingData.banner.titulo || '',
          banner_subtitulo: landingData.banner.subtitulo || '',
          banner_video_url: landingData.banner.videoUrl || '',
          banner_imagen_url: landingData.banner.imagenUrl || '',
          banner_boton_finalizar: landingData.banner.botonTextoFinalizar || 'Haz click para continuar',
          // Campos de botones de opciones (solo para campañas de video)
          banner_pregunta_texto: landingData.banner.preguntaTexto || '',
          banner_boton_a: landingData.banner.botonA || '',
          banner_boton_b: landingData.banner.botonB || '',
          banner_boton_c: landingData.banner.botonC || '',
          banner_boton_volver: landingData.banner.botonVolver || '',
          // Campos de servicios
        servicios_titulo: landingData.servicios.titulo || '',
        servicios_descripcion: landingData.servicios.descripcion || '',
        servicios_items: Array.isArray(landingData.servicios.items) ? landingData.servicios.items : [],
        servicios_fondo_imagen: landingData.servicios.fondoImagen || '',
        // Campos de dos columnas (imagen izquierda)
        dos_columnas_titulo: landingData.dosColumnas.titulo || '',
        dos_columnas_descripcion: landingData.dosColumnas.descripcion || '',
        dos_columnas_imagen: landingData.dosColumnas.imagen || '',
        dos_columnas_fondo_imagen: landingData.dosColumnas.fondoImagen || '',
          // Campos de dos columnas (imagen derecha)
          dos_columnas_derecha_titulo: landingData.dosColumnasDerecha.titulo || '',
          dos_columnas_derecha_descripcion: landingData.dosColumnasDerecha.descripcion || '',
          dos_columnas_derecha_imagen: landingData.dosColumnasDerecha.imagen || '',
          dos_columnas_derecha_fondo_imagen: landingData.dosColumnasDerecha.fondoImagen || '',
          // Campos de video (compatibilidad con Referidos)
          urlVideo: landingData.urlVideo || '',
          urlVideoProducto1: landingData.urlVideoProducto1 || '',
          urlVideoProducto2: landingData.urlVideoProducto2 || '',
          urlVideoProducto3: landingData.urlVideoProducto3 || '',
          urlBotonProducto: landingData.urlBotonProducto || '',
          urlComoUnirseCliente: landingData.urlComoUnirseCliente || '',
          urlNegocio1: landingData.urlNegocio1 || '',
          urlNegocio2: landingData.urlNegocio2 || '',
          urlNegocio3: landingData.urlNegocio3 || '',
          urlBotonNegocio: landingData.urlBotonNegocio || '',
          urlComoAfiliarse: landingData.urlComoAfiliarse || '',
          // Datos de Landing de Revelación
          revelacion_banner_titulo: revelacionLandingData.banner.titulo || '',
          revelacion_banner_subtitulo: revelacionLandingData.banner.subtitulo || '',
          revelacion_banner_video_url: revelacionLandingData.banner.videoUrl || '',
          revelacion_banner_imagen_url: revelacionLandingData.banner.imagenUrl || '',
          revelacion_servicios_titulo: revelacionLandingData.servicios.titulo || '',
          revelacion_servicios_descripcion: revelacionLandingData.servicios.descripcion || '',
          revelacion_servicios_items: Array.isArray(revelacionLandingData.servicios.items) ? revelacionLandingData.servicios.items : [],
          revelacion_servicios_fondo_imagen: revelacionLandingData.servicios.fondoImagen || '',
          revelacion_dos_columnas_titulo: revelacionLandingData.dosColumnas.titulo || '',
          revelacion_dos_columnas_descripcion: revelacionLandingData.dosColumnas.descripcion || '',
          revelacion_dos_columnas_imagen: revelacionLandingData.dosColumnas.imagen || '',
          revelacion_dos_columnas_fondo_imagen: revelacionLandingData.dosColumnas.fondoImagen || '',
          revelacion_dos_columnas_derecha_titulo: revelacionLandingData.dosColumnasDerecha.titulo || '',
          revelacion_dos_columnas_derecha_descripcion: revelacionLandingData.dosColumnasDerecha.descripcion || '',
          revelacion_dos_columnas_derecha_imagen: revelacionLandingData.dosColumnasDerecha.imagen || '',
          revelacion_dos_columnas_derecha_fondo_imagen: revelacionLandingData.dosColumnasDerecha.fondoImagen || '',
          // Datos de Landing de Éxito
          exito_banner_titulo: exitoLandingData.banner.titulo || '',
          exito_banner_subtitulo: exitoLandingData.banner.subtitulo || '',
          exito_banner_video_url: exitoLandingData.banner.videoUrl || '',
          exito_banner_imagen_url: exitoLandingData.banner.imagenUrl || '',
          exito_servicios_titulo: exitoLandingData.servicios.titulo || '',
          exito_servicios_descripcion: exitoLandingData.servicios.descripcion || '',
          exito_servicios_items: Array.isArray(exitoLandingData.servicios.items) ? exitoLandingData.servicios.items : [],
          exito_servicios_fondo_imagen: exitoLandingData.servicios.fondoImagen || '',
          exito_dos_columnas_titulo: exitoLandingData.dosColumnas.titulo || '',
          exito_dos_columnas_descripcion: exitoLandingData.dosColumnas.descripcion || '',
          exito_dos_columnas_imagen: exitoLandingData.dosColumnas.imagen || '',
          exito_dos_columnas_fondo_imagen: exitoLandingData.dosColumnas.fondoImagen || '',
          exito_dos_columnas_derecha_titulo: exitoLandingData.dosColumnasDerecha.titulo || '',
          exito_dos_columnas_derecha_descripcion: exitoLandingData.dosColumnasDerecha.descripcion || '',
          exito_dos_columnas_derecha_imagen: exitoLandingData.dosColumnasDerecha.imagen || '',
          exito_dos_columnas_derecha_fondo_imagen: exitoLandingData.dosColumnasDerecha.fondoImagen || '',
          adminId: adminId,
          adminNombre: adminNombre
        };
        endpoint = 'https://api.mrcirp.com/public/usuarios';
      } else {
        // Si viene de otra ruta, crear campaña de audiencia
        console.log('👥 Creando campaña de AUDIENCIA');
        console.log('Tab activo:', activeTab);
        console.log('Datos landing bienvenida:', landingData);
        console.log('Datos landing de agradecimiento:', whatsappLandingData);
        
        datosCompletos = {
        ...campanaData,
        ...landingData,
        adminId: adminId,
        adminNombre: adminNombre,
        // Mapear los datos del landing a los campos esperados por el API
        banner_titulo: landingData.banner.titulo,
        banner_subtitulo: landingData.banner.subtitulo,
        banner_video_url: landingData.banner.videoUrl,
        banner_imagen_url: landingData.banner.imagenUrl,
        banner_boton_finalizar: landingData.banner.botonTextoFinalizar || 'Haz click para continuar',
        // Campos de botones de opciones (solo para campañas de video)
        banner_pregunta_texto: landingData.banner.preguntaTexto || '',
        banner_boton_a: landingData.banner.botonA || '',
        banner_boton_b: landingData.banner.botonB || '',
        banner_boton_c: landingData.banner.botonC || '',
        banner_boton_volver: landingData.banner.botonVolver || '',
        servicios_titulo: landingData.servicios.titulo,
        servicios_descripcion: landingData.servicios.descripcion,
        servicios_items: landingData.servicios.items,
        servicios_fondo_imagen: landingData.servicios.fondoImagen || '',
        dos_columnas_titulo: landingData.dosColumnas.titulo,
        dos_columnas_descripcion: landingData.dosColumnas.descripcion,
        dos_columnas_imagen: landingData.dosColumnas.imagen,
        dos_columnas_fondo_imagen: landingData.dosColumnas.fondoImagen || '',
        dos_columnas_derecha_titulo: landingData.dosColumnasDerecha.titulo,
        dos_columnas_derecha_descripcion: landingData.dosColumnasDerecha.descripcion,
        dos_columnas_derecha_imagen: landingData.dosColumnasDerecha.imagen,
        dos_columnas_derecha_fondo_imagen: landingData.dosColumnasDerecha.fondoImagen || '',
        // Datos del landing de WhatsApp
        whatsapp_titulo: whatsappLandingData.titulo || '',
        whatsapp_subtitulo: whatsappLandingData.subtitulo || '',
        whatsapp_imagen_url: whatsappLandingData.imagenUrl || '',
        whatsapp_video_url: whatsappLandingData.videoUrl || '',
        whatsapp_boton_texto: whatsappLandingData.botonTexto || 'Dale Click Aquí',
        whatsapp_boton_url: whatsappLandingData.botonUrl || ''
        };
        endpoint = 'https://api.mrcirp.com/public/campanas-audiencia';
      }

      console.log('=== DATOS A ENVIAR ===');
      console.log('Endpoint:', endpoint);
      console.log('Datos completos:', JSON.stringify(datosCompletos, null, 2));
      console.log('Validación de campos requeridos:');
      console.log('- nombrecampana:', datosCompletos.nombrecampana || '❌ VACÍO');
      console.log('- nombre:', datosCompletos.nombre || '❌ VACÍO');
      console.log('- descripcion:', datosCompletos.descripcion || '❌ VACÍO');

      // Función para convertir base64 a archivo y subirlo
      const uploadBase64Image = async (base64String) => {
        if (!base64String || !base64String.startsWith('data:image/')) {
          return base64String; // Si no es base64, devolver la URL original
        }

        try {
          // Convertir base64 a blob
          const response = await fetch(base64String);
          const blob = await response.blob();
          
          // Determinar extensión desde el tipo MIME
          const mimeMatch = base64String.match(/data:image\/(\w+);base64/);
          const mimeType = mimeMatch ? mimeMatch[1] : 'jpeg';
          const extension = mimeType === 'jpeg' ? 'jpg' : mimeType;
          
          // Crear archivo desde blob
          const fileName = `campaign_${Date.now()}_${Math.random().toString(36).substring(7)}.${extension}`;
          const file = new File([blob], fileName, { type: `image/${mimeType}` });
          
          // Crear FormData
          const formData = new FormData();
          formData.append('file', file);
          formData.append('type', 'campaign');
          
          // Subir al servidor
          const uploadResponse = await axios.post(
            'https://api.mrcirp.com/public/upload-imagen-campana',
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          
          // Extraer URL de la respuesta
          let imageUrl = null;
          if (uploadResponse.data?.url) {
            imageUrl = uploadResponse.data.url;
          } else if (uploadResponse.data?.imagen) {
            imageUrl = uploadResponse.data.imagen;
          } else if (typeof uploadResponse.data === 'string') {
            imageUrl = uploadResponse.data;
          }
          
          if (imageUrl) {
            console.log(`✅ Imagen base64 subida exitosamente: ${imageUrl}`);
            return imageUrl;
          }
          
          console.warn('⚠️ No se recibió URL del servidor, manteniendo base64');
          return base64String;
        } catch (error) {
          console.error('❌ Error al subir imagen base64:', error);
          return base64String; // Si falla, mantener la base64
        }
      };

      // Procesar todas las imágenes base64 antes de enviar
      console.log('🔄 Procesando imágenes base64...');
      const imageFields = [
        'banner_imagen_url',
        'dos_columnas_imagen',
        'dos_columnas_derecha_imagen',
        'whatsapp_imagen_url',
        'revelacion_banner_imagen_url',
        'revelacion_dos_columnas_imagen',
        'revelacion_dos_columnas_derecha_imagen',
        'exito_banner_imagen_url',
        'exito_dos_columnas_imagen',
        'exito_dos_columnas_derecha_imagen'
      ];

      for (const field of imageFields) {
        if (datosCompletos[field] && datosCompletos[field].startsWith('data:image/')) {
          console.log(`📤 Subiendo imagen base64 del campo: ${field}`);
          datosCompletos[field] = await uploadBase64Image(datosCompletos[field]);
        }
      }

      console.log('✅ Todas las imágenes procesadas');

      const response = await axios.post(endpoint, datosCompletos);
      
      console.log('=== RESPUESTA DEL SERVIDOR ===');
      console.log('Status:', response.status);
      console.log('Response data:', JSON.stringify(response.data, null, 2));
      console.log('Response data type:', typeof response.data);
      console.log('Response headers:', response.headers);
      
      // El servidor puede devolver {success: true} o simplemente true
      const isSuccess = response.data === true || response.data?.success === true;
      
      if (isSuccess) {
        console.log('✅ Campaña creada exitosamente');
        console.log('ID de la campaña creada:', response.data?.id || response.data?.data?.id || 'No disponible');
        
        // Limpiar borrador después de guardar exitosamente
        limpiarBorrador();
        
        // Mostrar mensaje diferente según el tipo de campaña
        if (esReferidos) {
          alert('Campaña de video creada con éxito');
        } else {
        alert('¡Campaña de lanzamiento creada exitosamente!');
        }
        // Redirigir a mislanding
        navigate('/mislanding');
      } else {
        console.error('❌ Error en respuesta del servidor');
        console.error('Response data:', response.data);
        console.error('Response data type:', typeof response.data);
        const errorMessage = typeof response.data === 'object' && response.data?.message 
          ? response.data.message 
          : 'Error desconocido - El servidor no devolvió éxito';
        alert('Error al crear la campaña: ' + errorMessage);
      }
    } catch (error) {
      console.error('=== ERROR AL CREAR CAMPAÑA ===');
      console.error('Error completo:', error);
      console.error('Error message:', error.message);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      console.error('Error response headers:', error.response?.headers);
      console.error('Error config:', error.config);
      
      // Manejar error de límite de campañas (403)
      if (error.response?.status === 403 && error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert('Error al crear la campaña: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleVolver = () => {
    navigate('/seleccion-tipo-campana');
  };

  const handleEdit = (section) => {
    setEditingSection(section);
  };

  const handleSave = (section, data) => {
    if (section.includes('Revelacion')) {
      const sectionName = section.replace('Revelacion', '').toLowerCase();
      if (sectionName === 'banner') {
        setRevelacionLandingData(prev => ({ ...prev, banner: data }));
      } else if (sectionName === 'servicios') {
        setRevelacionLandingData(prev => ({ ...prev, servicios: data }));
      } else if (sectionName === 'doscolumnas') {
        setRevelacionLandingData(prev => ({ ...prev, dosColumnas: data }));
      } else if (sectionName === 'doscolumnasderecha') {
        setRevelacionLandingData(prev => ({ ...prev, dosColumnasDerecha: data }));
      }
    } else if (section.includes('Exito')) {
      const sectionName = section.replace('Exito', '').toLowerCase();
      if (sectionName === 'banner') {
        setExitoLandingData(prev => ({ ...prev, banner: data }));
      } else if (sectionName === 'servicios') {
        setExitoLandingData(prev => ({ ...prev, servicios: data }));
      } else if (sectionName === 'doscolumnas') {
        setExitoLandingData(prev => ({ ...prev, dosColumnas: data }));
      } else if (sectionName === 'doscolumnasderecha') {
        setExitoLandingData(prev => ({ ...prev, dosColumnasDerecha: data }));
      }
    } else {
      setLandingData(prev => ({
        ...prev,
        [section]: data
      }));
    }
    setEditingSection(null);
  };

  const handleCancel = () => {
    setEditingSection(null);
  };

  const renderBannerSection = () => {
    // Usar datos de vista previa temporal si se está editando
    const bannerPreviewData = previewBannerData || landingData.banner;
    
    return (
    <Card className="landing-section" title="🎬 Banner Principal" style={{ padding: 0 }}>
      <div 
        className="banner-preview"
        style={{
          backgroundImage: bannerPreviewData.imagenUrl ? `url(${bannerPreviewData.imagenUrl})` : 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '60px 24px',
          borderRadius: '8px 8px 0 0',
          minHeight: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}
      >
        <div className="banner-content" style={{ textAlign: 'center', zIndex: 1, position: 'relative' }}>
          <h1 
            dangerouslySetInnerHTML={{ __html: bannerPreviewData.titulo || 'Título Principal' }} 
            style={{ color: bannerPreviewData.imagenUrl ? 'white' : '#1890ff', textShadow: bannerPreviewData.imagenUrl ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none' }}
          />
          <p 
            dangerouslySetInnerHTML={{ __html: bannerPreviewData.subtitulo || 'Subtítulo descriptivo' }} 
            style={{ color: bannerPreviewData.imagenUrl ? 'white' : '#666', textShadow: bannerPreviewData.imagenUrl ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none' }}
          />
          {(esReferidos ? landingData.urlVideo : landingData.banner.videoUrl) && (
            <div className="video-container" style={{ marginTop: '24px', width: '600px', height: '350px', margin: '24px auto 0' }}>
              <iframe 
                src={convertToEmbedUrl(esReferidos ? landingData.urlVideo : landingData.banner.videoUrl)} 
                title="Video Banner"
                frameBorder="0"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '8px',
                  border: 'none'
                }}
              />
            </div>
          )}
        </div>
      </div>
      <Button 
        type="primary" 
        icon={<EditOutlined />} 
        onClick={() => handleEdit('banner')}
        className="edit-btn"
      >
        Editar Banner
      </Button>
    </Card>
    );
  };

  const renderServiciosSection = () => (
    <Card className="landing-section" title="🛠️ Sección de Servicios">
      <div 
        className="servicios-preview"
        style={{
          backgroundImage: landingData.servicios.fondoImagen ? `url(${landingData.servicios.fondoImagen})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          padding: landingData.servicios.fondoImagen ? '24px' : '0',
          borderRadius: landingData.servicios.fondoImagen ? '8px' : '0'
        }}
      >
        <h2 dangerouslySetInnerHTML={{ __html: landingData.servicios.titulo || '' }}></h2>
        <p dangerouslySetInnerHTML={{ __html: landingData.servicios.descripcion || '' }}></p>
        <Row gutter={[16, 16]}>
          {landingData.servicios.items.map((item, index) => (
            <Col xs={24} md={8} key={index}>
              <Card className="service-card">
                <h3 dangerouslySetInnerHTML={{ __html: item.titulo || '' }}></h3>
                <p dangerouslySetInnerHTML={{ __html: item.descripcion || '' }}></p>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
      <Button 
        type="primary" 
        icon={<EditOutlined />} 
        onClick={() => handleEdit('servicios')}
        className="edit-btn"
      >
        Editar Servicios
      </Button>
    </Card>
  );

  const renderDosColumnasSection = () => {
    // Usar datos de preview si se está editando, sino usar los datos reales
    const dosColumnasPreviewData = previewDosColumnasData || landingData.dosColumnas;
    
    return (
      <Card className="landing-section" title="📄 Sección de Dos Columnas (Imagen Izquierda)">
        <div 
          className="dos-columnas-preview"
          style={{
            backgroundImage: dosColumnasPreviewData.fondoImagen ? `url(${dosColumnasPreviewData.fondoImagen})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            padding: dosColumnasPreviewData.fondoImagen ? '24px' : '0',
            borderRadius: dosColumnasPreviewData.fondoImagen ? '8px' : '0'
          }}
        >
          <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            {(dosColumnasPreviewData.imagen && dosColumnasPreviewData.imagen.trim() !== '') ? (
              <img 
                src={dosColumnasPreviewData.imagen} 
                alt="Sección" 
                className="section-image"
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  minHeight: '200px',
                  backgroundColor: '#f0f0f0'
                }}
                onError={(e) => {
                  console.error('Error al cargar imagen:', dosColumnasPreviewData.imagen);
                  // No ocultar la imagen, solo mostrar un placeholder
                  e.target.style.display = 'none';
                  // Mostrar un mensaje de error visual
                  const parent = e.target.parentElement;
                  if (parent && !parent.querySelector('.image-error')) {
                    const errorDiv = document.createElement('div');
                    errorDiv.className = 'image-error';
                    errorDiv.style.cssText = 'padding: 20px; text-align: center; color: #999;';
                    errorDiv.textContent = 'Error al cargar la imagen';
                    parent.appendChild(errorDiv);
                  }
                }}
                onLoad={(e) => {
                  // Si la imagen se carga correctamente, ocultar cualquier mensaje de error
                  const parent = e.target.parentElement;
                  const errorDiv = parent?.querySelector('.image-error');
                  if (errorDiv) {
                    errorDiv.remove();
                  }
                }}
              />
            ) : (
              <div className="placeholder-image">
                <UploadOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                <p>Imagen de la sección</p>
              </div>
            )}
          </Col>
            <Col xs={24} md={12}>
              <h2 dangerouslySetInnerHTML={{ __html: dosColumnasPreviewData.titulo || '' }}></h2>
              <p dangerouslySetInnerHTML={{ __html: dosColumnasPreviewData.descripcion || '' }}></p>
            </Col>
          </Row>
        </div>
        <Button 
          type="primary" 
          icon={<EditOutlined />} 
          onClick={() => handleEdit('dosColumnas')}
          className="edit-btn"
        >
          Editar Sección
        </Button>
      </Card>
    );
  };

  const renderDosColumnasDerechaSection = () => {
    // Usar datos de preview si se está editando, sino usar los datos reales
    const dosColumnasDerechaPreviewData = previewDosColumnasDerechaData || landingData.dosColumnasDerecha;
    
    return (
      <Card className="landing-section" title="📄 Sección de Dos Columnas (Imagen Derecha)">
        <div 
          className="dos-columnas-preview"
          style={{
            backgroundImage: dosColumnasDerechaPreviewData.fondoImagen ? `url(${dosColumnasDerechaPreviewData.fondoImagen})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            padding: dosColumnasDerechaPreviewData.fondoImagen ? '24px' : '0',
            borderRadius: dosColumnasDerechaPreviewData.fondoImagen ? '8px' : '0'
          }}
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <h2 dangerouslySetInnerHTML={{ __html: dosColumnasDerechaPreviewData.titulo || '' }}></h2>
              <p dangerouslySetInnerHTML={{ __html: dosColumnasDerechaPreviewData.descripcion || '' }}></p>
            </Col>
            <Col xs={24} md={12}>
              {(dosColumnasDerechaPreviewData.imagen && dosColumnasDerechaPreviewData.imagen.trim() !== '') ? (
                <img 
                  src={dosColumnasDerechaPreviewData.imagen} 
                  alt="Sección" 
                  className="section-image"
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    minHeight: '200px',
                    backgroundColor: '#f0f0f0'
                  }}
                  onError={(e) => {
                    console.error('Error al cargar imagen:', dosColumnasDerechaPreviewData.imagen);
                    // No ocultar la imagen, solo mostrar un placeholder
                    e.target.style.display = 'none';
                    // Mostrar un mensaje de error visual
                    const parent = e.target.parentElement;
                    if (parent && !parent.querySelector('.image-error')) {
                      const errorDiv = document.createElement('div');
                      errorDiv.className = 'image-error';
                      errorDiv.style.cssText = 'padding: 20px; text-align: center; color: #999;';
                      errorDiv.textContent = 'Error al cargar la imagen';
                      parent.appendChild(errorDiv);
                    }
                  }}
                  onLoad={(e) => {
                    // Si la imagen se carga correctamente, ocultar cualquier mensaje de error
                    const parent = e.target.parentElement;
                    const errorDiv = parent?.querySelector('.image-error');
                    if (errorDiv) {
                      errorDiv.remove();
                    }
                  }}
                />
              ) : (
                <div className="placeholder-image">
                  <UploadOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                  <p>Imagen de la sección</p>
                </div>
              )}
            </Col>
          </Row>
        </div>
        <Button 
          type="primary" 
          icon={<EditOutlined />} 
          onClick={() => handleEdit('dosColumnasDerecha')}
          className="edit-btn"
        >
          Editar Sección
        </Button>
      </Card>
    );
  };

  const renderVideosPrincipales = () => (
    <Card className="landing-section" title="🎥 Videos Principales">
      <div className="editor-form">
        <div className="form-group">
          <label>URL del video de bienvenida</label>
          <Input 
            value={landingData.urlVideo}
            onChange={(e) => setLandingData({...landingData, urlVideo: e.target.value})}
            placeholder="YouTube o Vimeo URL"
          />
        </div>
      </div>
    </Card>
  );

  const renderZonaRevelacion = () => (
    <Card className="landing-section" title="🔍 Zona de Revelación">
      <div className="editor-form">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <div className="form-group">
              <label>URL Zona de revelación 1</label>
              <Input 
                value={landingData.urlVideoProducto1}
                onChange={(e) => setLandingData({...landingData, urlVideoProducto1: e.target.value})}
                placeholder="Video URL"
              />
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div className="form-group">
              <label>URL Zona de revelación 2</label>
              <Input 
                value={landingData.urlVideoProducto2}
                onChange={(e) => setLandingData({...landingData, urlVideoProducto2: e.target.value})}
                placeholder="Video URL"
              />
            </div>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <div className="form-group">
              <label>URL Zona de revelación 3</label>
              <Input 
                value={landingData.urlVideoProducto3}
                onChange={(e) => setLandingData({...landingData, urlVideoProducto3: e.target.value})}
                placeholder="Video URL"
              />
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div className="form-group">
              <label>URL Botón Zona de revelación</label>
              <Input 
                value={landingData.urlBotonProducto}
                onChange={(e) => setLandingData({...landingData, urlBotonProducto: e.target.value})}
                placeholder="Enlace del botón"
              />
            </div>
          </Col>
        </Row>
        <div className="form-group">
          <label>URL Video de agradecimiento producto</label>
          <Input 
            value={landingData.urlComoUnirseCliente}
            onChange={(e) => setLandingData({...landingData, urlComoUnirseCliente: e.target.value})}
            placeholder="Video de agradecimiento"
          />
        </div>
      </div>
    </Card>
  );

  const renderZonaExito = () => (
    <Card className="landing-section" title="🏆 Zona de Éxito">
      <div className="editor-form">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <div className="form-group">
              <label>URL Zona de éxito 1</label>
              <Input 
                value={landingData.urlNegocio1}
                onChange={(e) => setLandingData({...landingData, urlNegocio1: e.target.value})}
                placeholder="Video URL"
              />
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div className="form-group">
              <label>URL Zona de éxito 2</label>
              <Input 
                value={landingData.urlNegocio2}
                onChange={(e) => setLandingData({...landingData, urlNegocio2: e.target.value})}
                placeholder="Video URL"
              />
            </div>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <div className="form-group">
              <label>URL Zona de éxito 3</label>
              <Input 
                value={landingData.urlNegocio3}
                onChange={(e) => setLandingData({...landingData, urlNegocio3: e.target.value})}
                placeholder="Video URL"
              />
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div className="form-group">
              <label>URL Botón Zona de éxito</label>
              <Input 
                value={landingData.urlBotonNegocio}
                onChange={(e) => setLandingData({...landingData, urlBotonNegocio: e.target.value})}
                placeholder="Enlace del botón"
              />
            </div>
          </Col>
        </Row>
        <div className="form-group">
          <label>URL Video de agradecimiento negocio</label>
          <Input 
            value={landingData.urlComoAfiliarse}
            onChange={(e) => setLandingData({...landingData, urlComoAfiliarse: e.target.value})}
            placeholder="Video de agradecimiento"
          />
        </div>
      </div>
    </Card>
  );

  // Funciones de renderizado para Landing de Revelación
  const renderBannerSectionRevelacion = () => (
    <Card className="landing-section" title="🎬 Banner Principal" style={{ padding: 0 }}>
      <div 
        className="banner-preview"
        style={{
          backgroundImage: revelacionLandingData.banner.imagenUrl ? `url(${revelacionLandingData.banner.imagenUrl})` : 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '60px 24px',
          borderRadius: '8px 8px 0 0',
          minHeight: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}
      >
        <div className="banner-content" style={{ textAlign: 'center', zIndex: 1, position: 'relative' }}>
          <h1 
            dangerouslySetInnerHTML={{ __html: revelacionLandingData.banner.titulo || 'Título Principal' }} 
            style={{ color: revelacionLandingData.banner.imagenUrl ? 'white' : '#1890ff', textShadow: revelacionLandingData.banner.imagenUrl ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none' }}
          />
          <p 
            dangerouslySetInnerHTML={{ __html: revelacionLandingData.banner.subtitulo || 'Subtítulo descriptivo' }} 
            style={{ color: revelacionLandingData.banner.imagenUrl ? 'white' : '#666', textShadow: revelacionLandingData.banner.imagenUrl ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none' }}
          />
        </div>
      </div>
      <Button 
        type="primary" 
        icon={<EditOutlined />} 
        onClick={() => handleEdit('bannerRevelacion')}
        className="edit-btn"
      >
        Editar Banner
      </Button>
    </Card>
  );

  const renderServiciosSectionRevelacion = () => (
    <Card className="landing-section" title="🛠️ Sección de Servicios">
      <div 
        className="servicios-preview"
        style={{
          backgroundImage: revelacionLandingData.servicios.fondoImagen ? `url(${revelacionLandingData.servicios.fondoImagen})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          padding: revelacionLandingData.servicios.fondoImagen ? '24px' : '0',
          borderRadius: revelacionLandingData.servicios.fondoImagen ? '8px' : '0'
        }}
      >
        <h2 dangerouslySetInnerHTML={{ __html: revelacionLandingData.servicios.titulo || '' }}></h2>
        <p dangerouslySetInnerHTML={{ __html: revelacionLandingData.servicios.descripcion || '' }}></p>
        <Row gutter={[16, 16]}>
          {revelacionLandingData.servicios.items.map((item, index) => (
            <Col xs={24} md={8} key={index}>
              <Card className="service-card">
                <h3 dangerouslySetInnerHTML={{ __html: item.titulo || '' }}></h3>
                <p dangerouslySetInnerHTML={{ __html: item.descripcion || '' }}></p>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
      <Button 
        type="primary" 
        icon={<EditOutlined />} 
        onClick={() => handleEdit('serviciosRevelacion')}
        className="edit-btn"
      >
        Editar Servicios
      </Button>
    </Card>
  );

  const renderDosColumnasSectionRevelacion = () => (
    <Card className="landing-section" title="📄 Sección de Dos Columnas (Imagen Izquierda)">
      <div 
        className="dos-columnas-preview"
        style={{
          backgroundImage: revelacionLandingData.dosColumnas.fondoImagen ? `url(${revelacionLandingData.dosColumnas.fondoImagen})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          padding: revelacionLandingData.dosColumnas.fondoImagen ? '24px' : '0',
          borderRadius: revelacionLandingData.dosColumnas.fondoImagen ? '8px' : '0'
        }}
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            {revelacionLandingData.dosColumnas.imagen ? (
              <img src={revelacionLandingData.dosColumnas.imagen} alt="Sección" className="section-image" />
            ) : (
              <div className="placeholder-image">
                <UploadOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                <p>Imagen de la sección</p>
              </div>
            )}
          </Col>
          <Col xs={24} md={12}>
            <h2 dangerouslySetInnerHTML={{ __html: revelacionLandingData.dosColumnas.titulo || '' }}></h2>
            <p dangerouslySetInnerHTML={{ __html: revelacionLandingData.dosColumnas.descripcion || '' }}></p>
          </Col>
        </Row>
      </div>
      <Button 
        type="primary" 
        icon={<EditOutlined />} 
        onClick={() => handleEdit('dosColumnasRevelacion')}
        className="edit-btn"
      >
        Editar Sección
      </Button>
    </Card>
  );

  const renderDosColumnasDerechaSectionRevelacion = () => (
    <Card className="landing-section" title="📄 Sección de Dos Columnas (Imagen Derecha)">
      <div className="dos-columnas-preview">
        <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            <h2 dangerouslySetInnerHTML={{ __html: revelacionLandingData.dosColumnasDerecha.titulo || '' }}></h2>
            <p dangerouslySetInnerHTML={{ __html: revelacionLandingData.dosColumnasDerecha.descripcion || '' }}></p>
          </Col>
          <Col xs={24} md={12}>
            {revelacionLandingData.dosColumnasDerecha.imagen ? (
              <img src={revelacionLandingData.dosColumnasDerecha.imagen} alt="Sección" className="section-image" />
            ) : (
              <div className="placeholder-image">
                <UploadOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                <p>Imagen de la sección</p>
              </div>
            )}
          </Col>
        </Row>
      </div>
      <Button 
        type="primary" 
        icon={<EditOutlined />} 
        onClick={() => handleEdit('dosColumnasDerechaRevelacion')}
        className="edit-btn"
      >
        Editar Sección
      </Button>
    </Card>
  );

  // Funciones de renderizado para Landing de Éxito
  const renderBannerSectionExito = () => (
    <Card className="landing-section" title="🎬 Banner Principal" style={{ padding: 0 }}>
      <div 
        className="banner-preview"
        style={{
          backgroundImage: exitoLandingData.banner.imagenUrl ? `url(${exitoLandingData.banner.imagenUrl})` : 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '60px 24px',
          borderRadius: '8px 8px 0 0',
          minHeight: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}
      >
        <div className="banner-content" style={{ textAlign: 'center', zIndex: 1, position: 'relative' }}>
          <h1 
            dangerouslySetInnerHTML={{ __html: exitoLandingData.banner.titulo || 'Título Principal' }} 
            style={{ color: exitoLandingData.banner.imagenUrl ? 'white' : '#1890ff', textShadow: exitoLandingData.banner.imagenUrl ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none' }}
          />
          <p 
            dangerouslySetInnerHTML={{ __html: exitoLandingData.banner.subtitulo || 'Subtítulo descriptivo' }} 
            style={{ color: exitoLandingData.banner.imagenUrl ? 'white' : '#666', textShadow: exitoLandingData.banner.imagenUrl ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none' }}
          />
        </div>
      </div>
      <Button 
        type="primary" 
        icon={<EditOutlined />} 
        onClick={() => handleEdit('bannerExito')}
        className="edit-btn"
      >
        Editar Banner
      </Button>
    </Card>
  );

  const renderServiciosSectionExito = () => (
    <Card className="landing-section" title="🛠️ Sección de Servicios">
      <div 
        className="servicios-preview"
        style={{
          backgroundImage: exitoLandingData.servicios.fondoImagen ? `url(${exitoLandingData.servicios.fondoImagen})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          padding: exitoLandingData.servicios.fondoImagen ? '24px' : '0',
          borderRadius: exitoLandingData.servicios.fondoImagen ? '8px' : '0'
        }}
      >
        <h2 dangerouslySetInnerHTML={{ __html: exitoLandingData.servicios.titulo || '' }}></h2>
        <p dangerouslySetInnerHTML={{ __html: exitoLandingData.servicios.descripcion || '' }}></p>
        <Row gutter={[16, 16]}>
          {exitoLandingData.servicios.items.map((item, index) => (
            <Col xs={24} md={8} key={index}>
              <Card className="service-card">
                <h3 dangerouslySetInnerHTML={{ __html: item.titulo || '' }}></h3>
                <p dangerouslySetInnerHTML={{ __html: item.descripcion || '' }}></p>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
      <Button 
        type="primary" 
        icon={<EditOutlined />} 
        onClick={() => handleEdit('serviciosExito')}
        className="edit-btn"
      >
        Editar Servicios
      </Button>
    </Card>
  );

  const renderDosColumnasSectionExito = () => (
    <Card className="landing-section" title="📄 Sección de Dos Columnas (Imagen Izquierda)">
      <div 
        className="dos-columnas-preview"
        style={{
          backgroundImage: exitoLandingData.dosColumnas.fondoImagen ? `url(${exitoLandingData.dosColumnas.fondoImagen})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          padding: exitoLandingData.dosColumnas.fondoImagen ? '24px' : '0',
          borderRadius: exitoLandingData.dosColumnas.fondoImagen ? '8px' : '0'
        }}
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            {exitoLandingData.dosColumnas.imagen ? (
              <img src={exitoLandingData.dosColumnas.imagen} alt="Sección" className="section-image" />
            ) : (
              <div className="placeholder-image">
                <UploadOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                <p>Imagen de la sección</p>
              </div>
            )}
          </Col>
          <Col xs={24} md={12}>
            <h2 dangerouslySetInnerHTML={{ __html: exitoLandingData.dosColumnas.titulo || '' }}></h2>
            <p dangerouslySetInnerHTML={{ __html: exitoLandingData.dosColumnas.descripcion || '' }}></p>
          </Col>
        </Row>
      </div>
      <Button 
        type="primary" 
        icon={<EditOutlined />} 
        onClick={() => handleEdit('dosColumnasExito')}
        className="edit-btn"
      >
        Editar Sección
      </Button>
    </Card>
  );

  const renderDosColumnasDerechaSectionExito = () => (
    <Card className="landing-section" title="📄 Sección de Dos Columnas (Imagen Derecha)">
      <div className="dos-columnas-preview">
        <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            <h2 dangerouslySetInnerHTML={{ __html: exitoLandingData.dosColumnasDerecha.titulo || '' }}></h2>
            <p dangerouslySetInnerHTML={{ __html: exitoLandingData.dosColumnasDerecha.descripcion || '' }}></p>
          </Col>
          <Col xs={24} md={12}>
            {exitoLandingData.dosColumnasDerecha.imagen ? (
              <img src={exitoLandingData.dosColumnasDerecha.imagen} alt="Sección" className="section-image" />
            ) : (
              <div className="placeholder-image">
                <UploadOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                <p>Imagen de la sección</p>
              </div>
            )}
          </Col>
        </Row>
      </div>
      <Button 
        type="primary" 
        icon={<EditOutlined />} 
        onClick={() => handleEdit('dosColumnasDerechaExito')}
        className="edit-btn"
      >
        Editar Sección
      </Button>
    </Card>
  );

  // Renderizar el formulario del landing de WhatsApp
  const renderWhatsappLanding = () => {
    return (
      <div>
        <Card className="landing-section" title="📱 Configuración Landing de Agradecimiento">
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <div className="form-group">
                <label><strong>Título Principal</strong></label>
                <RichTextEditor
                  value={whatsappLandingData.titulo || ''}
                  onChange={(html) => setWhatsappLandingData({...whatsappLandingData, titulo: html})}
                  placeholder="Ingresa el título principal"
                  maxLength={500}
                />
                <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
                  {(whatsappLandingData.titulo ? whatsappLandingData.titulo.replace(/<[^>]*>/g, '') : '').length}/500 caracteres
                </Text>
              </div>
            </Col>
            <Col xs={24}>
              <div className="form-group">
                <label><strong>Subtítulo</strong></label>
                <RichTextEditor
                  value={whatsappLandingData.subtitulo || ''}
                  onChange={(html) => setWhatsappLandingData({...whatsappLandingData, subtitulo: html})}
                  placeholder="Ingresa el subtítulo"
                  maxLength={500}
                />
                <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
                  {(whatsappLandingData.subtitulo ? whatsappLandingData.subtitulo.replace(/<[^>]*>/g, '') : '').length}/500 caracteres
                </Text>
              </div>
            </Col>
            <Col xs={24}>
              <div className="form-group">
                <ImageUploader
                  value={whatsappLandingData.imagenUrl || ''}
                  onChange={(url) => setWhatsappLandingData({...whatsappLandingData, imagenUrl: url})}
                  label="URL de Imagen del Banner"
                  placeholder="https://ejemplo.com/imagen.jpg o sube una imagen"
                  recommendedSize="1920px x 1080px"
                  autoUpload={false}
                />
              </div>
            </Col>
            <Col xs={24}>
              <div className="form-group">
                <label><strong>URL del Video (YouTube/Vimeo)</strong></label>
                <Input 
                  value={whatsappLandingData.videoUrl} 
                  onChange={(e) => setWhatsappLandingData({...whatsappLandingData, videoUrl: e.target.value})}
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div className="form-group">
                <label><strong>Texto del Botón</strong></label>
                <Input 
                  value={whatsappLandingData.botonTexto} 
                  onChange={(e) => setWhatsappLandingData({...whatsappLandingData, botonTexto: e.target.value})}
                  placeholder="Dale Click Aquí"
                />
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div className="form-group">
                <label><strong>URL del Botón</strong></label>
                <Input 
                  value={whatsappLandingData.botonUrl} 
                  onChange={(e) => setWhatsappLandingData({...whatsappLandingData, botonUrl: e.target.value})}
                  placeholder="https://ejemplo.com"
                />
              </div>
            </Col>
          </Row>
        </Card>

        {/* Vista previa del landing de WhatsApp */}
        <Card className="landing-section" title="👁️ Vista Previa">
          <div 
            className="whatsapp-banner-preview"
            style={{
              backgroundImage: whatsappLandingData.imagenUrl ? `url(${whatsappLandingData.imagenUrl})` : 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              padding: '60px 24px',
              borderRadius: '8px',
              minHeight: '300px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            <div style={{ textAlign: 'center', zIndex: 1, position: 'relative', width: '100%' }}>
              <h1 
                dangerouslySetInnerHTML={{ __html: whatsappLandingData.titulo || 'Título Principal' }} 
                style={{ 
                  color: whatsappLandingData.imagenUrl ? 'white' : 'white', 
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                  marginBottom: '16px'
                }}
              />
              <p 
                dangerouslySetInnerHTML={{ __html: whatsappLandingData.subtitulo || 'Subtítulo descriptivo' }} 
                style={{ 
                  color: whatsappLandingData.imagenUrl ? 'white' : 'white', 
                  textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                  marginBottom: '24px'
                }}
              />
              {whatsappLandingData.videoUrl && (
                <div className="video-container" style={{ marginTop: '24px', width: '100%', maxWidth: '600px', height: '350px', margin: '0 auto 24px' }}>
                  <iframe 
                    src={convertToEmbedUrl(whatsappLandingData.videoUrl)} 
                    title="Video Banner"
                    frameBorder="0"
                    allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '8px',
                      border: 'none'
                    }}
                  />
                </div>
              )}
              {whatsappLandingData.botonUrl && (
                <Button 
                  type="primary" 
                  size="large"
                  href={whatsappLandingData.botonUrl}
                  target="_blank"
                  style={{
                    background: '#25D366',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 32px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    height: 'auto',
                    marginTop: '16px'
                  }}
                >
                  {whatsappLandingData.botonTexto || 'Dale Click Aquí'}
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>
    );
  };

  const renderEditModal = () => {
    if (!editingSection) return null;

    return (
      <Modal
        title={`Editar ${editingSection}`}
        open={true}
        onCancel={handleCancel}
        footer={null}
        width={800}
      >
        {editingSection === 'banner' && (
          <BannerEditor 
            data={landingData.banner} 
            onSave={(data) => {
              handleSave('banner', data);
              setPreviewBannerData(null); // Limpiar vista previa temporal
            }}
            onCancel={() => {
              handleCancel();
              setPreviewBannerData(null); // Limpiar vista previa temporal
            }}
            isVideoCampaign={esReferidos}
            urlVideoBienvenida={landingData.urlVideo}
            onUrlVideoBienvenidaChange={(e) => setLandingData({...landingData, urlVideo: e.target.value})}
            onPreviewUpdate={(data) => setPreviewBannerData(data)} // Actualizar vista previa en tiempo real
            isBienvenida={true}
          />
        )}
        {editingSection === 'servicios' && (
          <ServiciosEditor 
            data={landingData.servicios} 
            onSave={(data) => handleSave('servicios', data)}
            onCancel={handleCancel}
          />
        )}
        {editingSection === 'dosColumnas' && (
          <DosColumnasEditor 
            data={landingData.dosColumnas} 
            onSave={(data) => {
              handleSave('dosColumnas', data);
              setPreviewDosColumnasData(null); // Limpiar vista previa temporal
            }}
            onCancel={() => {
              handleCancel();
              setPreviewDosColumnasData(null); // Limpiar vista previa temporal
            }}
            onPreviewUpdate={(data) => setPreviewDosColumnasData(data)} // Actualizar vista previa en tiempo real
          />
        )}
        {editingSection === 'dosColumnasDerecha' && (
          <DosColumnasEditor 
            data={landingData.dosColumnasDerecha} 
            onSave={(data) => {
              handleSave('dosColumnasDerecha', data);
              setPreviewDosColumnasDerechaData(null); // Limpiar vista previa temporal
            }}
            onCancel={() => {
              handleCancel();
              setPreviewDosColumnasDerechaData(null); // Limpiar vista previa temporal
            }}
            onPreviewUpdate={(data) => setPreviewDosColumnasDerechaData(data)} // Actualizar vista previa en tiempo real
          />
        )}
        {/* Secciones de Revelación */}
        {editingSection === 'bannerRevelacion' && (
          <BannerEditor 
            data={revelacionLandingData.banner} 
            onSave={(data) => handleSave('bannerRevelacion', data)}
            onCancel={handleCancel}
          />
        )}
        {editingSection === 'serviciosRevelacion' && (
          <ServiciosEditor 
            data={revelacionLandingData.servicios} 
            onSave={(data) => handleSave('serviciosRevelacion', data)}
            onCancel={handleCancel}
          />
        )}
        {editingSection === 'dosColumnasRevelacion' && (
          <DosColumnasEditor 
            data={revelacionLandingData.dosColumnas} 
            onSave={(data) => handleSave('dosColumnasRevelacion', data)}
            onCancel={handleCancel}
          />
        )}
        {editingSection === 'dosColumnasDerechaRevelacion' && (
          <DosColumnasEditor 
            data={revelacionLandingData.dosColumnasDerecha} 
            onSave={(data) => handleSave('dosColumnasDerechaRevelacion', data)}
            onCancel={handleCancel}
          />
        )}
        {/* Secciones de Éxito */}
        {editingSection === 'bannerExito' && (
          <BannerEditor 
            data={exitoLandingData.banner} 
            onSave={(data) => handleSave('bannerExito', data)}
            onCancel={handleCancel}
          />
        )}
        {editingSection === 'serviciosExito' && (
          <ServiciosEditor 
            data={exitoLandingData.servicios} 
            onSave={(data) => handleSave('serviciosExito', data)}
            onCancel={handleCancel}
          />
        )}
        {editingSection === 'dosColumnasExito' && (
          <DosColumnasEditor 
            data={exitoLandingData.dosColumnas} 
            onSave={(data) => handleSave('dosColumnasExito', data)}
            onCancel={handleCancel}
          />
        )}
        {editingSection === 'dosColumnasDerechaExito' && (
          <DosColumnasEditor 
            data={exitoLandingData.dosColumnasDerecha} 
            onSave={(data) => handleSave('dosColumnasDerechaExito', data)}
            onCancel={handleCancel}
          />
        )}
      </Modal>
    );
  };

  // Verificar si hay borrador guardado
  const [hayBorrador, setHayBorrador] = useState(false);
  
  useEffect(() => {
    const verificarBorrador = () => {
      const borradorJSON = window.localStorage.getItem(BORRADOR_KEY);
      setHayBorrador(!!borradorJSON);
    };
    verificarBorrador();
    // Verificar cada vez que se guarde
    const interval = setInterval(verificarBorrador, 1000);
    return () => clearInterval(interval);
  }, [BORRADOR_KEY]);

  return (
    <div>
      <Sidebar>
        <div className="campana-audiencia-container">
          {hayBorrador && (
            <div style={{
              background: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)',
              border: '1px solid #91d5ff',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '18px' }}>💾</span>
                <Text style={{ margin: 0 }}>
                  <strong>Borrador guardado automáticamente</strong> - Tu progreso se guarda cada 2 segundos
                </Text>
              </div>
              <Button 
                size="small" 
                onClick={limpiarBorrador}
                style={{ marginLeft: '16px' }}
              >
                Descartar Borrador
              </Button>
            </div>
          )}
          <div className="page-header">
            <Title level={2} className="page-title">
              {esReferidos ? '🎬 Maquetador de Campaña de Videos' : '👥 Maquetador de Campaña de Lanzamiento'}
            </Title>
            <Text className="page-subtitle">
              {esReferidos ? 'Crea tu campaña de video con nuestro editor visual' : 'Crea tu landing page personalizada con nuestro editor visual'}
            </Text>
            <div className="header-actions">
              <Button 
                type="default" 
                icon={<ArrowLeftOutlined />} 
                onClick={handleVolver}
              >
                Volver
              </Button>
            </div>
          </div>

            <div className="editor-mode">
              {/* Información Básica de la Campaña */}
              <Card className="landing-section" title="📋 Información Básica de la Campaña">
                <Collapse 
                  activeKey={openBasico ? ['1'] : []} 
                  onChange={(key) => setOpenBasico(key.includes('1'))}
                >
                  <Panel header="📋 Información Básica" key="1">
                    <Row gutter={[16, 16]}>
                      <Col xs={24} md={12}>
                        <div className="form-group">
                          <label><strong>Nombre de la campaña</strong></label>
                          <Input
                            className="mb-2"
                            type="text"
                            name="nombrecampana"
                            value={campanaData.nombrecampana}
                            onChange={handleChangeCampana}
                            placeholder="Ej: Mi Campaña 2024"
                          />
                        </div>
                      </Col>
                      <Col xs={24} md={12}>
                        <div className="form-group">
                          <label><strong>Enlace de la campaña</strong></label>
                          <Input
                            className="mb-2"
                            type="text"
                            name="nombre"
                            value={campanaData.nombre}
                            onChange={handleChangeCampana}
                            placeholder="minombre (sin espacios)"
                          />
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            katanasite.app/minombre
                          </Text>
                          <br />
                          <Text style={{ color: colorVerificacion, fontSize: '12px' }}>
                            {mensajeVerificacion}
                          </Text>
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={24}>
                        <div className="form-group">
                          <label><strong>Descripción de la campaña</strong></label>
                          <Input
                            value={campanaData.descripcion ? campanaData.descripcion.replace(/<[^>]*>/g, '') : ''}
                            onChange={(e) => {
                              setCampanaData(prevState => ({
                                ...prevState,
                                descripcion: e.target.value
                              }));
                            }}
                            placeholder="Describe tu campaña (máximo 150 caracteres)"
                            maxLength={150}
                          />
                          <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
                            {(campanaData.descripcion ? campanaData.descripcion.replace(/<[^>]*>/g, '') : '').length}/150 caracteres
                          </Text>
                        </div>
                      </Col>
                    </Row>
                  </Panel>
                </Collapse>
              </Card>

              {/* Tabs para campañas de audiencia */}
              {!esReferidos && (
                <Card className="landing-section" style={{ marginTop: '24px' }}>
                  <Tabs 
                    activeKey={activeTab} 
                    onChange={setActiveTab}
                    style={{
                      '--ant-tabs-active-color': '#1890ff',
                    }}
                    items={[
                      {
                        key: 'bienvenida',
                        label: 'Landing Bienvenida',
                        children: (
                          <div>
              {renderBannerSection()}
              {renderServiciosSection()}
              {renderDosColumnasSection()}
              {renderDosColumnasDerechaSection()}
                          </div>
                        )
                      },
                      {
                        key: 'whatsapp',
                        label: 'Landing de Agradecimiento',
                        children: renderWhatsappLanding()
                      }
                    ]}
                  />
                  <style>{`
                    .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
                      background-color: #1890ff !important;
                      color: white !important;
                      border-radius: 4px 4px 0 0;
                      padding: 8px 16px;
                    }
                    .ant-tabs-tab {
                      border-radius: 4px 4px 0 0;
                    }
                    .ant-tabs-tab:hover {
                      color: #1890ff;
                    }
                  `}</style>
                </Card>
              )}

              {/* Tabs para campañas de video (referidos) */}
              {esReferidos && (
                <Card className="landing-section" style={{ marginTop: '24px' }}>
                  <Tabs 
                    activeKey={activeTab} 
                    onChange={setActiveTab}
                    style={{
                      '--ant-tabs-active-color': '#52c41a',
                    }}
                    items={[
                      {
                        key: 'bienvenida',
                        label: 'Landing Bienvenida',
                        children: (
                          <div>
                            {renderBannerSection()}
                            {renderServiciosSection()}
                            {renderDosColumnasSection()}
                            {renderDosColumnasDerechaSection()}
                          </div>
                        )
                      },
                      {
                        key: 'revelacion',
                        label: 'Landing de Producto',
                        children: (
                          <div>
                            {renderZonaRevelacion()}
                            {renderBannerSectionRevelacion()}
                            {renderServiciosSectionRevelacion()}
                            {renderDosColumnasSectionRevelacion()}
                            {renderDosColumnasDerechaSectionRevelacion()}
                          </div>
                        )
                      },
                      {
                        key: 'exito',
                        label: 'Landing de Negocio',
                        children: (
                          <div>
                            {renderZonaExito()}
                            {renderBannerSectionExito()}
                            {renderServiciosSectionExito()}
                            {renderDosColumnasSectionExito()}
                            {renderDosColumnasDerechaSectionExito()}
                          </div>
                        )
                      }
                    ]}
                  />
                  <style>{`
                    .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
                      background-color: #52c41a !important;
                      color: white !important;
                      border-radius: 4px 4px 0 0;
                      padding: 8px 16px;
                    }
                    .ant-tabs-tab {
                      border-radius: 4px 4px 0 0;
                    }
                    .ant-tabs-tab:hover {
                      color: #52c41a;
                    }
                  `}</style>
                </Card>
              )}
              
              {/* Botón para crear la campaña */}
              <Card className="landing-section">
                <div className="text-center">
                  <Button 
                    type="primary" 
                    size="large"
                    onClick={handleCrearCampana}
                    style={{ 
                      background: 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 32px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      height: 'auto'
                    }}
                  >
                    {esReferidos ? '💾 Guardar Campaña de Video' : '🚀 Crear Campaña de Lanzamiento'}
                  </Button>
                  <div style={{ marginTop: '12px' }}>
                    <Text type="secondary">
                      Al crear la campaña, se guardará toda la información del landing page
                    </Text>
                  </div>
                </div>
              </Card>
                </div>

          {renderEditModal()}
        </div>
      </Sidebar>
    </div>
  );
};

// Componentes de edición
const BannerEditor = ({ data, onSave, onCancel, isVideoCampaign, urlVideoBienvenida, onUrlVideoBienvenidaChange, onPreviewUpdate, isBienvenida = false }) => {
  const [formData, setFormData] = useState(data);

  // Actualizar vista previa en tiempo real
  useEffect(() => {
    if (onPreviewUpdate) {
      onPreviewUpdate(formData);
    }
  }, [formData, onPreviewUpdate]);

  const handleSubmit = () => {
    onSave(formData);
  };

  return (
    <div className="editor-form">
      <div className="form-group">
        <label>Título Principal</label>
        <RichTextEditor
          value={formData.titulo || ''}
          onChange={(html) => setFormData({...formData, titulo: html})}
          placeholder="Ingresa el título principal"
          maxLength={500}
        />
        <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
          {(formData.titulo ? formData.titulo.replace(/<[^>]*>/g, '') : '').length}/500 caracteres
        </Text>
      </div>
      <div className="form-group">
        <label>Subtítulo</label>
        <RichTextEditor
          value={formData.subtitulo || ''}
          onChange={(html) => setFormData({...formData, subtitulo: html})}
          placeholder="Ingresa el subtítulo"
          maxLength={500}
        />
        <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
          {(formData.subtitulo ? formData.subtitulo.replace(/<[^>]*>/g, '') : '').length}/500 caracteres
        </Text>
      </div>
      {/* Campo de URL del video - solo visible en banner de bienvenida para campañas de video */}
      {isVideoCampaign && isBienvenida && (
        <div className="form-group">
          <label>URL del video de bienvenida</label>
          <Input 
            value={urlVideoBienvenida || ''} 
            onChange={onUrlVideoBienvenidaChange}
            placeholder="YouTube o Vimeo URL"
          />
        </div>
      )}
      {/* Campo de URL del video para campañas de audiencia (no video) en banner de bienvenida */}
      {!isVideoCampaign && isBienvenida && (
        <div className="form-group">
          <label>URL del video de bienvenida</label>
          <Input 
            value={formData.videoUrl || ''} 
            onChange={(e) => setFormData({...formData, videoUrl: e.target.value})}
            placeholder="YouTube o Vimeo URL"
          />
        </div>
      )}
      {/* Campo de texto del botón al finalizar video - solo para banner de bienvenida */}
      {isBienvenida && (
        <div className="form-group">
          <label>Botón al finalizar video</label>
          <Input 
            value={formData.botonTextoFinalizar || ''} 
            onChange={(e) => setFormData({...formData, botonTextoFinalizar: e.target.value})}
            placeholder="Haz click para continuar"
          />
          <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
            Texto que aparecerá en el botón cuando finalice el video
          </Text>
        </div>
      )}
      {/* Campos para editar textos de botones de opciones - solo para campañas de video en banner de bienvenida */}
      {isVideoCampaign && isBienvenida && (
        <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e8e8e8' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontWeight: 'bold', fontSize: '14px', display: 'block', marginBottom: '4px' }}>
              📝 Textos de Botones de Opciones
            </label>
            <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>
              Personaliza los textos que aparecerán después del video principal
            </Text>
          </div>
          <Row gutter={[12, 12]}>
            <Col xs={24}>
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                  Texto de la Pregunta
                </label>
                <Input 
                  size="small"
                  value={formData.preguntaTexto || ''} 
                  onChange={(e) => setFormData({...formData, preguntaTexto: e.target.value})}
                  placeholder="¿Qué le pareció el video? … ¿Sorprendente cierto? ¿Como deseas continuar?"
                />
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                  Botón A
                </label>
                <Input 
                  size="small"
                  value={formData.botonA || ''} 
                  onChange={(e) => setFormData({...formData, botonA: e.target.value})}
                  placeholder="SÍ QUIERO MÁS INFORMACIÓN DEL PRODUCTO"
                />
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                  Botón B
                </label>
                <Input 
                  size="small"
                  value={formData.botonB || ''} 
                  onChange={(e) => setFormData({...formData, botonB: e.target.value})}
                  placeholder="QUIERO CONOCER MÁS DEL NEGOCIO"
                />
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                  Botón C
                </label>
                <Input 
                  size="small"
                  value={formData.botonC || ''} 
                  onChange={(e) => setFormData({...formData, botonC: e.target.value})}
                  placeholder="NO GRACIAS, DESEO SALIR"
                />
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                  Botón Volver
                </label>
                <Input 
                  size="small"
                  value={formData.botonVolver || ''} 
                  onChange={(e) => setFormData({...formData, botonVolver: e.target.value})}
                  placeholder="VOLVER"
                />
              </div>
            </Col>
          </Row>
        </div>
      )}
      <div className="form-group">
        <ImageUploader
          value={formData.imagenUrl || ''}
          onChange={(url) => setFormData({...formData, imagenUrl: url})}
          label="URL de Imagen"
          placeholder="https://ejemplo.com/imagen.jpg o sube una imagen"
          recommendedSize="1920px x 1080px"
          autoUpload={false}
        />
      </div>
      
      <div className="form-actions">
        <Button onClick={onCancel}>Cancelar</Button>
        <Button type="primary" onClick={handleSubmit}>Guardar</Button>
      </div>
    </div>
  );
};

const ServiciosEditor = ({ data, onSave, onCancel }) => {
  const [formData, setFormData] = useState(data);

  const handleSubmit = () => {
    onSave(formData);
  };

  const updateService = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  return (
    <div className="editor-form">
      <div className="form-group">
        <label>Imagen de Fondo de la Sección</label>
        <ImageUploader
          value={formData.fondoImagen || ''}
          onChange={(url) => setFormData({...formData, fondoImagen: url})}
          label=""
          placeholder="Sube una imagen de fondo o ingresa una URL"
          autoUpload={false}
        />
        <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
          Esta imagen se mostrará como fondo de toda la sección de servicios
        </Text>
      </div>
      <div className="form-group">
        <label>Título de la Sección</label>
        <RichTextEditor
          value={formData.titulo || ''}
          onChange={(html) => setFormData({...formData, titulo: html})}
          placeholder="Título de los servicios"
          maxLength={200}
        />
        <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
          {(formData.titulo ? formData.titulo.replace(/<[^>]*>/g, '') : '').length}/200 caracteres
        </Text>
      </div>
      <div className="form-group">
        <label>Descripción General</label>
        <RichTextEditor
          value={formData.descripcion || ''}
          onChange={(html) => setFormData({...formData, descripcion: html})}
          placeholder="Descripción de los servicios"
          maxLength={1000}
        />
        <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
          {(formData.descripcion ? formData.descripcion.replace(/<[^>]*>/g, '') : '').length}/1000 caracteres
        </Text>
      </div>
      {formData.items.map((item, index) => (
        <div key={index} className="service-editor">
          <h4>Servicio {index + 1}</h4>
          <div className="form-group">
            <label>Título</label>
            <RichTextEditor
              value={item.titulo || ''}
              onChange={(html) => updateService(index, 'titulo', html)}
              placeholder="Título del servicio"
              maxLength={200}
            />
            <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
              {(item.titulo ? item.titulo.replace(/<[^>]*>/g, '') : '').length}/200 caracteres
            </Text>
          </div>
          <div className="form-group">
            <label>Descripción</label>
            <RichTextEditor
              value={item.descripcion || ''}
              onChange={(html) => updateService(index, 'descripcion', html)}
              placeholder="Descripción del servicio"
              maxLength={500}
            />
            <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
              {(item.descripcion ? item.descripcion.replace(/<[^>]*>/g, '') : '').length}/500 caracteres
            </Text>
          </div>
        </div>
      ))}
      <div className="form-actions">
        <Button onClick={onCancel}>Cancelar</Button>
        <Button type="primary" onClick={handleSubmit}>Guardar</Button>
      </div>
    </div>
  );
};

const DosColumnasEditor = ({ data, onSave, onCancel, onPreviewUpdate }) => {
  const [formData, setFormData] = useState(data);
  const [localImageUrl, setLocalImageUrl] = useState(null); // Mantener URL local (base64) como respaldo

  // Actualizar vista previa en tiempo real
  useEffect(() => {
    if (onPreviewUpdate) {
      // Si hay URL local, usarla para la vista previa, sino usar la del formData
      const previewData = localImageUrl 
        ? {...formData, imagen: localImageUrl}
        : formData;
      onPreviewUpdate(previewData);
    }
  }, [formData, localImageUrl, onPreviewUpdate]);

  const handleSubmit = () => {
    onSave(formData);
  };

  return (
    <div className="editor-form">
      <div className="form-group">
        <label>Imagen de Fondo de la Sección</label>
        <ImageUploader
          value={formData.fondoImagen || ''}
          onChange={(url) => setFormData({...formData, fondoImagen: url})}
          label=""
          placeholder="Sube una imagen de fondo o ingresa una URL"
          autoUpload={false}
        />
        <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
          Esta imagen se mostrará como fondo de toda la sección de dos columnas
        </Text>
      </div>
      <div className="form-group">
        <label>Título de la Sección</label>
        <RichTextEditor
          value={formData.titulo || ''}
          onChange={(html) => setFormData({...formData, titulo: html})}
          placeholder="Título de la sección"
          maxLength={200}
        />
        <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
          {(formData.titulo ? formData.titulo.replace(/<[^>]*>/g, '') : '').length}/200 caracteres
        </Text>
      </div>
      <div className="form-group">
        <label>Descripción</label>
        <RichTextEditor
          value={formData.descripcion || ''}
          onChange={(html) => setFormData({...formData, descripcion: html})}
          placeholder="Descripción de la sección"
          maxLength={1000}
        />
        <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
          {(formData.descripcion ? formData.descripcion.replace(/<[^>]*>/g, '') : '').length}/1000 caracteres
        </Text>
      </div>
      <div className="form-group">
        <ImageUploader
          value={formData.imagen || ''}
          onChange={(url) => {
            // Si es una URL base64 (local), guardarla como respaldo
            if (url && url.startsWith('data:image')) {
              setLocalImageUrl(url);
            }
            // Actualizar el formData con la URL (puede ser local o del servidor)
            const newFormData = {...formData, imagen: url};
            setFormData(newFormData);
            // Actualizar el preview inmediatamente (priorizando URL local si existe)
            if (onPreviewUpdate) {
              const previewData = url.startsWith('data:image') 
                ? {...newFormData, imagen: url}
                : (localImageUrl ? {...newFormData, imagen: localImageUrl} : newFormData);
              onPreviewUpdate(previewData);
            }
          }}
          label="URL de la Imagen"
          placeholder="https://ejemplo.com/imagen.jpg o sube una imagen"
          autoUpload={false}
        />
      </div>
      <div className="form-actions">
        <Button onClick={onCancel}>Cancelar</Button>
        <Button type="primary" onClick={handleSubmit}>Guardar</Button>
      </div>
    </div>
  );
};

export default CampanaAudiencia;
